import { Observable, Subject } from './addRxOperators';
import querystring from 'querystring';
import camelCase from 'lodash/camelCase';

import ContactListItem from './ContactListItem';
import ContactDetails from './ContactDetails';
import Action from './Action';
import Discount from './Discount';
import History from './History';
import Bonus from './Bonus';

const avHost = 'https://AccountView.net/api/v3';
// const avHost = 'https://av-proxy.azurewebsites.net/api/v3';

let contactsStore;

function formatProperties(data) {
  return data.map(json => {
    const result = {};
    Object.keys(json).forEach(key => (result[camelCase(key)] = json[key]));
    return result;
  });
}

function filterArrToObject(items) {
  return items.reduce((filter, item, i) => {
    const nr = i + 1;
    filter['filterControlSource' + nr] = item.controlSource;
    filter['filterOperator' + nr] = item.operator || 'equal';
    filter['filterValueType' + nr] = item.valueType || 'C';
    filter['filterValue' + nr] = item.value;
    filter['filterIsListOfValues' + nr] = item.isListOfValues || false;
    return filter;
  }, {});
}

function fetchContacts(text) {
  return Observable.of(text)
    .map(text => {
      var filter = [
        { controlSource: 'rpl_grp.grp_ext', valueType: 'L', value: '.T.' }
      ];

      if (text) {
        filter.push({
          controlSource: 'acct_name',
          operator: 'contains',
          value: text
        });
      }
      return filterArrToObject(filter);
    })
    .map(filter =>
      Object.assign({}, filter, {
        businessObject: 'CM1',
        pageSize: 50,
        fields: [
          'sub_nr',
          'acct_name',
          'main_cont',
          'city',
          'deb_show',
          'cred_show',
          'k1pp_code',
          'k1pp_name'
        ].join(','),
        sortFields: 'acct_name',
        sortOrder: 'ascending'
      })
    )
    .map(query => querystring.encode(query))
    .map(query => `${avHost}/AccountViewData?${query}`)
    .switchMap(url =>
      Observable.ajax({
        url: url,
        headers: {
          authorization: `${contactsStore.token_type} ${contactsStore.access_token}`,
          'x-company': contactsStore.company_id
        }
      })
    )
    .map(rsp => rsp.response)
    .map(body => body.CONTACT)
    .map(contacts => formatProperties(contacts))
    .map(contacts =>
      contacts.map(json => ContactListItem.fromJSON(json.subNr, json))
    )
    .catch(err => {
      contactsStore.fetchError(err);
      return Observable.of([]);
    });
}

const search$ = new Subject();
search$
  .do(text => (contactsStore.filterText = text))
  .debounceTime(500)
  .distinctUntilChanged()
  .do(() => contactsStore.requestStart('c'))
  .switchMap(text => fetchContacts(text))
  .do(
    () => contactsStore.requestDone('c'),
    () => contactsStore.requestDone('c')
  )
  .subscribe(
    contacts => (contactsStore.contactsList = contacts),
    err => contactsStore.fetchError(err)
  );

export default class ContactsQueries {
  constructor(store) {
    contactsStore = store;
  }

  fetchCompanyId() {
    const companies$ = Observable.ajax({
      url: `${avHost}/companies`,
      headers: {
        authorization: `${contactsStore.token_type} ${contactsStore.access_token}`
      }
    })
      .publish()
      .refCount()
      .map(rsp => rsp.response);

    companies$.subscribe(
      c => {
        contactsStore.company_id = c[0].Id;
        localStorage.company_id = c[0].Id;

        if (!contactsStore.contactsList.length) {
          contactsStore.search();
        }
      },
      err => contactsStore.fetchError(err)
    );

    return companies$;
  }

  search(text = '') {
    search$.next(text);
  }

  fetchContactDetails(subNr) {
    return Observable.of(subNr)
      .map(subNr =>
        filterArrToObject([{ controlSource: 'sub_nr', value: subNr }])
      )
      .map(filter =>
        Object.assign({}, filter, { businessObject: 'CM1', pageSize: 1 })
      )
      .map(query => querystring.encode(query))
      .map(query => `${avHost}/AccountViewData?${query}`)
      .do(() => contactsStore.requestStart('d'))
      .switchMap(url =>
        Observable.ajax({
          url: url,
          headers: {
            authorization: `${contactsStore.token_type} ${contactsStore.access_token}`,
            'x-company': contactsStore.company_id
          }
        })
      )
      .do(
        () => contactsStore.requestDone('d'),
        () => contactsStore.requestDone('d')
      )
      .map(rsp => rsp.response)
      .map(body => body.CONTACT)
      .map(contacts => formatProperties(contacts))
      .flatMap(contacts => contacts)
      .map(json => ContactDetails.fromJSON(json.subNr, json))
      .catch(err => {
        contactsStore.fetchError(err);
        return Observable.of({});
      });
  }

  fetchActions(subNr) {
    return Observable.of(subNr)
      .map(subNr =>
        filterArrToObject([
          { controlSource: 'sub_nr', value: subNr },
          {
            controlSource: 'act_stat',
            operator: 'NotEqual',
            valueType: 'N',
            value: 3
          }
        ])
      )
      .map(filter =>
        Object.assign({}, filter, {
          businessObject: 'AC1',
          pageSize: 100,
          SortFields: 'area_main,act_start',
          SortOrder: 'ascending',
          fields: [
            'act_code',
            'sub_nr',
            'area_main',
            'area_sub',
            'act_stat',
            'emp_nr',
            'act_abs',
            'act_desc',
            'act_start',
            'act_finish',
            'act_prior'
          ].join(',')
        })
      )
      .map(query => querystring.encode(query))
      .map(query => `${avHost}/AccountViewData?${query}`)
      .do(() => contactsStore.requestStart('a'))
      .switchMap(url =>
        Observable.ajax({
          url: url,
          headers: {
            authorization: `${contactsStore.token_type} ${contactsStore.access_token}`,
            'x-company': contactsStore.company_id
          }
        })
      )
      .do(
        () => contactsStore.requestDone('a'),
        () => contactsStore.requestDone('a')
      )
      .map(rsp => rsp.response)
      .map(body => body.ACTION)
      .map(actions => formatProperties(actions))
      .map(actions => actions.map(json => Action.fromJSON(json.actCode, json)))
      .map(actions => {
        const price = actions
          .filter(a => a.areaMain === 'PRIJS')
          .sort((x, y) => y.actStart - x.actStart);
        const others = actions
          .filter(a => a.areaMain !== 'PRIJS')
          .sort((x, y) => {
            if (x.areaMain === y.areaMain) {
              return y.actStart - x.actStart;
            } else if (x.areaMain < y.areaMain) {
              return -1;
            } else if (x.areaMain > y.areaMain) {
              return 1;
            }
            return 0;
          });
        return price.concat(others);
      })
      .catch(err => {
        contactsStore.fetchError(err);
        return Observable.of({});
      });
  }

  fetchDiscounts(subNr) {
    return Observable.of(subNr)
      .map(subNr =>
        filterArrToObject([{ controlSource: 'sub_nr', value: subNr }])
      )
      .map(filter =>
        Object.assign({}, filter, {
          businessObject: 'QD1',
          pageSize: 1000,
          SortFields: 'art_code, ship_mth',
          SortOrder: 'ascending',
          fields: [
            'qty_disc.rec_id',
            'qty_disc.art_code',
            'qty_disc.ship_mth',
            'qty_disc.qd_desc',
            'qty_disc.vart_desc1',
            'qty_disc.beg_date',
            'qty_disc.end_date',
            'qty_disc.disc_code',
            'qty_disc.cost_inkpu',
            'qty_disc.k1_inkpct',
            'qty_disc.k1_krtpct',
            'qty_disc.k1packcont',
            'qty_disc.v_bon_pct',
            'qd_qty.rec_id',
            'qd_qty.qd_id',
            'qd_qty.qd_qty',
            'qd_qty.qd_val',
            'qd_qty.k1_calcpx'
          ].join(',')
        })
      )
      .map(query => querystring.encode(query))
      .map(query => `${avHost}/AccountViewData?${query}`)
      .do(() => contactsStore.requestStart('q'))
      .switchMap(url =>
        Observable.ajax({
          url: url,
          headers: {
            authorization: `${contactsStore.token_type} ${contactsStore.access_token}`,
            'x-company': contactsStore.company_id
          }
        })
      )
      .do(
        () => contactsStore.requestDone('q'),
        () => contactsStore.requestDone('q')
      )
      .map(rsp => rsp.response)
      .map(body =>
        body.QTY_DISC.map(disc => {
          disc.qty = formatProperties(
            body.QD_QTY.filter(qty => qty.QD_ID === disc.REC_ID)
          );
          return disc;
        })
      )
      .map(formatProperties)
      .map(actions => actions.map(json => Discount.fromJSON(json.recId, json)))
      .catch(err => {
        contactsStore.fetchError(err);
        return Observable.of({});
      });
  }

  fetchHistory(subNr) {
    return Observable.of(subNr)
      .map(subNr =>
        filterArrToObject([{ 
          controlSource: 'hst_ah.rpl_inv', value: subNr 
        }, {
          controlSource: 'hst_ah.hdr_type', value: '1,2' , valueType:'N', isListOfValues: true
        }])
      )
      .map(filter =>
        Object.assign({}, filter, {
          businessObject: 'ALH',
          pageSize: 20,
          SortFields: 'hst_ah.inv_date',
          SortOrder: 'descending',
          fields: [
            'hst_al.rec_id',
            'hst_ah.hdr_type',
            'hst_ah.rpl_inv',
            'hst_ah.inv_date',
            'hst_al.art_code',
            'hst_ah.ship_mth',
            'hst_al.unit_px',
            'hst_al.art_unit',
            'hst_al.vpack_qty',
            'hst_al.pack_cont',
            'hst_al.k1_saw',
            'hst_al.k1_marge',
            'hst_al.cost_px',
            'hst_al.l_costpx',
            'hst_ah.po_dlb',
            'hst_al.direct_px',
            'hst_al.vsrc_code',
            'hst_ah.pack_qty',
            'hst_ah.vdisc_desc',
            'hst_ah.ship_cost',
            'hst_ah.ord_cost',
            'hst_ah.comment1',
            'hst_ah.del_addr',
            'hst_ah.comment2'
          ].join(',')
        })
      )
      .map(query => querystring.encode(query))
      .map(query => `${avHost}/AccountViewData?${query}`)
      .do(() => contactsStore.requestStart('h'))
      .switchMap(url =>
        Observable.ajax({
          url: url,
          headers: {
            authorization: `${contactsStore.token_type} ${contactsStore.access_token}`,
            'x-company': contactsStore.company_id
          }
        })
      )
      .do(
        () => contactsStore.requestDone('h'),
        () => contactsStore.requestDone('h')
      )
      .map(rsp => rsp.response)
      .map(body => body.HST_AL)
      .map(formatProperties)
      .map(history => history.map(json => History.fromJSON(json.recId, json)))
      .catch(err => {
        contactsStore.fetchError(err);
        return Observable.of({});
      });
  }

  fetchBonuses(subNr) {
    return Observable.of(subNr)
      .map(subNr =>
        filterArrToObject([{ controlSource: 'k1_bonpr.sub_nr', value: subNr }])
      )
      .map(filter =>
        Object.assign({}, filter, {
          businessObject: 'K1BP',
          pageSize: 100
        })
      )
      .map(query => querystring.encode(query))
      .map(query => `${avHost}/AccountViewData?${query}`)
      .do(() => contactsStore.requestStart('i'))
      .switchMap(url =>
        Observable.ajax({
          url: url,
          headers: {
            authorization: `${contactsStore.token_type} ${contactsStore.access_token}`,
            'x-company': contactsStore.company_id
          }
        })
      )
      .do(
        () => contactsStore.requestDone('i'),
        () => contactsStore.requestDone('i')
      )
      .map(rsp => rsp.response)
      .map(body => body.K1_BONPR)
      .map(formatProperties)
      .map(bonuses => bonuses.map(json => Bonus.fromJSON(json.recId, json)))
      .catch(err => {
        contactsStore.fetchError(err);
        return Observable.of({});
      });
  }
}



// WEBPACK FOOTER //
// ./src/store/ContactsQueries.js